import { useAppDispatch, useAppSelector } from "@app/hooks";
import {
  useCreateSectionMutation,
  useReorderSectionsMutation,
  useToggleSectionVisibilityMutation,
} from "@services/survey-sections";
import { scrollToSection } from "@shared/utils";
import Analytics from "@shared/utils/tagManagerAnalytics";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import type { SurveySection } from "@survey-types/survey.section.types";
import { reorderSections, updateCurrentSurvey } from "@survey/surveySlice";

// Type for section update operations
type SectionUpdatePayload = Pick<SurveySection, "surveySectionId" | "isHidden">;

export const useSectionOperations = () => {
  const dispatch = useAppDispatch();
  const { currentSurvey } = useAppSelector((state) => state.survey);

  // RTK Query mutations
  const [createSection] = useCreateSectionMutation();
  const [toggleSectionVisibility] = useToggleSectionVisibilityMutation();
  const [reorderSectionsMutation] = useReorderSectionsMutation();

  // Get sections from current survey
  const sections = currentSurvey?.sections || [];

  // Update section (toggle visibility)
  const handleUpdateSection = async (
    section: SectionUpdatePayload,
    title: string,
  ) => {
    if (!currentSurvey?.id || !sections.length) return;
    Analytics.send({
      event: "Toggle section visibility",
      SurveyID: currentSurvey.id,
      SectionName: title,
    });

    // toggleSectionVisibility handles optimistic updates for both
    // RTK cache and Redux slice without invalidatesTags (no refetch flicker)
    toggleSectionVisibility({
      sectionId: section.surveySectionId,
      isHidden: section.isHidden,
    });
  };

  // Create new section
  const handleCreateNewSection = async () => {
    if (!currentSurvey?.id) return null;

    const result = await createSection({
      surveyId: currentSurvey.id,
      name: "Untitled Section",
      isCustom: true,
    })
      .unwrap()
      .catch((_error) => {
        console.error("Failed to create section");
        return null;
      });

    if (!result) return null;

    // Optimistic update: Add the new section to Redux store immediately
    const newSection: SurveySection = {
      surveySectionId: result.surveySectionId,
      surveyId: currentSurvey.id,
      name: "Untitled Section",
      isCustom: true,
      isHidden: false,
      isReorderable: true,
      isDeletable: true,
      isRenamable: true,
      hasPDF: false,
      order: sections.length,
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
      pdfs: [],
    };

    dispatch(updateCurrentSurvey({ sections: [...sections, newSection] }));
    return result.surveySectionId;
  };

  // Reorder sections by index
  const handleReorderByIndex = async (
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (!currentSurvey?.id || sourceIndex === destinationIndex) return;

    // Optimistic update using Redux action (handles array reordering)
    dispatch(reorderSections({ sourceIndex, destinationIndex }));
    setTimeout(() => {
      scrollToSection(sections[sourceIndex].surveySectionId);
    }, 100);
    reorderSectionsMutation({
      surveyId: currentSurvey.id,
      order: destinationIndex,
      surveySectionId: sections[sourceIndex].surveySectionId,
    })
      .unwrap()
      .then((res: DetailedSurvey) => {
        const sections = res?.sections ? [...res.sections] : [];
        const order = sections
          ?.sort((a, b) => a.order - b.order)
          ?.map((s) => s.name);

        // Defer analytics event to avoid GTM initialization race condition
        // Use setTimeout to ensure GTM is ready before attempting to send
        setTimeout(() => {
          try {
            Analytics.send({
              event: "Reorder Survey Sections",
              SurveyID: res.id,
              SurveyName: res.name,
              order,
            });
          } catch (_error) {
            console.error("Failed to send analytics event");
          }
        }, 0);
      })
      .catch((_error) => {
        console.error("Failed to reorder sections");
        // Revert optimistic update on error
        dispatch(
          reorderSections({
            sourceIndex: destinationIndex,
            destinationIndex: sourceIndex,
          }),
        );
      });
  };

  return {
    // State
    sections,
    currentSurvey,
    // Operations
    handleUpdateSection,
    handleCreateNewSection,
    handleReorderByIndex,
  };
};
